import React, { ReactNode } from 'react'
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { Modal } from '../../common/Modal'
import { ModalBody } from '../../common/Modal/ModalBody'
import { ModalHeader } from '../../common/Modal/ModalHeader'
import {
  CreateEditPostModalContent,
  PostEditSeed,
} from './CreateEditPostModalContent'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { societyCreatePostSchema } from '../../../forms/schemas/society_create_post'
import { NPosts } from '../../../interfaces/services/posts.interfaces'
import { ToastType } from '../../common/Toast/toast-type'
import {
  create,
  postQueries,
  postRowQueries,
  postScheduledQueryKey,
  togglePin,
  update,
} from '../../../api/posts'
import { invalidatePublishedPost } from '../postRowCache'
import { usePinFailureToast } from '../useTogglePin'

interface CreateEditPostModalProps {
  postId?: string
  attachments?: PostEditSeed
  show: boolean
  close: () => void
}

export type ScheduleChange = 'schedule' | 'reschedule' | 'publishNow' | 'edit'

const scheduleToast = {
  schedule: 'scheduledPosts.flashMessage.scheduleSuccess',
  reschedule: 'scheduledPosts.flashMessage.rescheduleSuccess',
  publishNow: 'scheduledPosts.flashMessage.publishNowSuccess',
} as const

export const publishTimeBody = (
  publishAt: Date | null | undefined,
  scheduledAt: number | undefined,
  updateMode: boolean
): { publishAt?: string | null } => {
  if (updateMode && scheduledAt === undefined) {
    return {}
  }
  if (updateMode && publishAt?.getTime() === scheduledAt) {
    return {}
  }
  if (!publishAt) {
    return updateMode ? { publishAt: null } : {}
  }
  return { publishAt: publishAt.toISOString() }
}

export const savedScheduleChange = (
  updateMode: boolean,
  body: Pick<NPosts.NPatch.IRequestBody, 'publishAt'>,
  isScheduled?: boolean
): ScheduleChange | undefined => {
  if (!updateMode) {
    return isScheduled ? 'schedule' : undefined
  }
  if (body.publishAt !== undefined) {
    return isScheduled ? 'reschedule' : 'publishNow'
  }
  return isScheduled ? 'edit' : undefined
}

export const refreshAfterSave = (
  queryClient: QueryClient,
  change?: ScheduleChange,
  id?: string
): void => {
  if (!change) {
    queryClient.invalidateQueries()
    return
  }
  if (change === 'schedule' || !id) {
    queryClient.invalidateQueries({ queryKey: postScheduledQueryKey })
    return
  }
  queryClient.invalidateQueries({
    queryKey: postQueries.getPost(id).queryKey,
  })
  if (change === 'publishNow') {
    invalidatePublishedPost(queryClient, id)
  } else {
    queryClient.invalidateQueries({ queryKey: postScheduledQueryKey })
    queryClient.invalidateQueries({
      queryKey: postRowQueries.row(id).queryKey,
    })
  }
}

// A failed pin must not fail the save, so it resolves to the error.
const pinAfterSave = (id: string, value: boolean): Promise<unknown> =>
  togglePin(id, value).then(
    () => null,
    (error: unknown) => error
  )

export const CreateEditPostModal = ({
  postId,
  attachments,
  show,
  close,
}: CreateEditPostModalProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const queryClient = useQueryClient()
  const showPinFailure = usePinFailureToast()
  const { data: post } = useQuery(postQueries.getPost(postId))

  const updateMode = !!postId

  const { mutate: mutateCreatePost, isPending: createPending } = useMutation({
    mutationFn: async ({
      body,
      pin,
    }: {
      body: NPosts.NCreate.IRequestBody
      pin: boolean
    }) => {
      const created = (await create(body)).data.data
      const pinError =
        pin && created ? await pinAfterSave(created._id, true) : null
      return {
        change: savedScheduleChange(false, body, created?.isScheduled),
        pinError,
      }
    },
    meta: { skipGlobalInvalidate: true },
    onSuccess: ({ change, pinError }) => {
      setToastNotification(
        ToastType.SUCCESS,
        translate(
          change === 'schedule'
            ? scheduleToast.schedule
            : 'postCreateUpdateBase.flashMessage.createPostSuccess'
        )
      )
      refreshAfterSave(queryClient, change)
      if (pinError) {
        showPinFailure(pinError)
      }
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('postCreateUpdateBase.flashMessage.createPostFailure')
      )
    },
  })

  const { mutate: mutatePatchPost, isPending: patchPending } = useMutation({
    mutationFn: async ({
      id,
      body,
      pin,
    }: {
      id: string
      body: NPosts.NPatch.IRequestBody
      pin?: boolean
    }) => {
      const updated = (await update(id, body)).data.data
      const pinError = pin === undefined ? null : await pinAfterSave(id, pin)
      return {
        change: savedScheduleChange(true, body, updated?.isScheduled),
        pinError,
      }
    },
    meta: { skipGlobalInvalidate: true },
    onSuccess: ({ change, pinError }, { id }) => {
      setToastNotification(
        ToastType.SUCCESS,
        translate(
          change && change !== 'edit'
            ? scheduleToast[change]
            : 'postCreateUpdateBase.flashMessage.updatePostSuccess'
        )
      )
      refreshAfterSave(queryClient, change, id)
      if (pinError) {
        showPinFailure(pinError)
      }
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('postCreateUpdateBase.flashMessage.updatePostFailure')
      )
    },
  })

  const onSubmit = async (
    data: InferType<typeof societyCreatePostSchema>
  ): Promise<void> => {
    const { publishAt, pinned = false, ...fields } = data
    const schedule = publishTimeBody(
      publishAt,
      attachments?.isScheduled ? attachments.createdAt : undefined,
      updateMode
    )
    if (updateMode) {
      const body = { ...fields, ...schedule } as NPosts.NPatch.IRequestBody
      const pinChanged = pinned !== !!post?.pinnedAt
      mutatePatchPost({
        id: postId,
        body,
        pin: pinChanged ? pinned : undefined,
      })
    } else {
      const body = { ...fields, ...schedule } as NPosts.NCreate.IRequestBody
      mutateCreatePost({ body, pin: pinned })
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    if (updateMode) {
      setToastNotification(
        ToastType.DANGER,
        translate('postCreateUpdateBase.flashMessage.updatePostFailure')
      )
    } else {
      setToastNotification(
        ToastType.DANGER,
        translate('postCreateUpdateBase.flashMessage.createPostFailure')
      )
    }
  }

  const loading = createPending || patchPending

  return (
    <Modal show={show} size="xl">
      {{
        header: (
          <ModalHeader onClose={close}>
            {translate('feedView.modalTitle')}
          </ModalHeader>
        ),
        body: (
          <ModalBody className="flex h-[850px]" padding={false}>
            {!updateMode && (
              <CreateEditPostModalContent
                post={post}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
              />
            )}
            {updateMode && post && (
              <CreateEditPostModalContent
                post={post}
                attachments={attachments}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
              />
            )}
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
